import * as React from 'react';
import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import type { TransitionProps } from '@mui/material/transitions';
import cn from 'classnames';
import Fade from '@mui/material/Fade';

import { useTheme } from '../../theme';
import FullScreenDialogHeader from './Header/Header';
import { useBlurActiveElement } from '../../hooks';

export interface Props {
	isOpen: boolean;
	title?: string;
	onClose: () => void;
	rootDialogClassName?: string;
	headerClassName?: string;
	closeClassName?: string;
	contentClassName?: string;
	fadeTransition?: boolean;
	keepMounted?: boolean;
	mountOnEnter?: boolean;
	hideHeader?: boolean;
	backButtonRender?: (onClose: () => void) => React.ReactNode;
	children?: React.ReactNode;
}

const SlideUp = React.forwardRef<React.FC, TransitionProps>((props, ref) => (
	<Slide direction="up" {...props} ref={ref} children={props.children as React.ReactElement<any, any>} />
));

const FadeTransition = React.forwardRef<React.FC, TransitionProps>((props, ref) => (
	<Fade {...props} ref={ref} children={props.children as React.ReactElement<any, any>} />
));

const FullscreenDialog = React.forwardRef<HTMLDivElement, Props>(
	(
		{
			mountOnEnter,
			isOpen,
			children,
			title,
			headerClassName,
			closeClassName,
			contentClassName,
			fadeTransition,
			keepMounted,
			onClose,
			rootDialogClassName,
			hideHeader,
			backButtonRender
		},
		ref
	) => {
		const { FullscreenDialog: theme } = useTheme('FullscreenDialog');

		const [dialogIsMounted, setDialogIsMounted] = useState(!mountOnEnter);

		/**
		 * WEBSKY-5566:
		 * Перед закрытием модалки/диалогового окна, снимаем фокус с активного элемента (если есть),
		 * чтобы избежать ошибки "blocked aria-hidden".
		 */
		useBlurActiveElement(isOpen);

		useEffect(() => {
			if (isOpen && !dialogIsMounted) {
				setDialogIsMounted(true);
			}
		}, [isOpen, dialogIsMounted]);

		return (
			<Dialog
				open={isOpen}
				TransitionComponent={!fadeTransition ? SlideUp : FadeTransition}
				fullScreen={true}
				disableEnforceFocus={true}
				closeAfterTransition={false}
				onClose={onClose}
				classes={{
					root: cn(theme.withoutPadding, rootDialogClassName)
				}}
				keepMounted={keepMounted}
			>
				{!hideHeader && (
					<FullScreenDialogHeader
						onClose={onClose}
						title={title}
						headerClassName={headerClassName}
						closeClassName={closeClassName}
					/>
				)}

				{dialogIsMounted && (
					<div
						ref={ref}
						className={cn(theme.content, contentClassName, {
							[theme.content_withoutOffset]: hideHeader
						})}
					>
						{children}
					</div>
				)}
			</Dialog>
		);
	}
);

export default FullscreenDialog;
